import { Modal, Pending } from '@cluey/cluey-components';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { api } from '../../../api';
import { Enrolment } from '../../../api/types/enrolment';
import { isGroupSessionCheck } from '../../../util/helpers';
import { PATH_HUB_MS_STUDENTS_AND_SUBJECTS } from '../../../util/pagePath';
import { OptionCard, OptionCardContent } from '../../EnrolmentManagement/OptionCard';
import { IntentIntro } from '../../IntentIntro';
import { Loader } from '../../Loader/Loader';

interface Props {
  enrolmentLinkTo(enrolment: Enrolment): string;
  closeModal?(): void;
  open: boolean;
  openInNewTab: boolean;
  onSelect?(enrolment: Enrolment, link: string): void;
  position: 'top' | 'middle' | 'bottom';
  subtitle?: string;
  filterEnrolmentType?: string;
  state?: Record<string, string>;
}

const SelectEnrolmentModal = (props: Props) => {
  const { data: enrolments, isLoading: loadingEnrolments } = api.enrolment.all.useQuery('active');
  const {
    open,
    enrolmentLinkTo,
    closeModal,
    openInNewTab,
    onSelect,
    position = 'middle',
    subtitle,
    filterEnrolmentType,
    state,
  } = props;
  const history = useHistory();
  const hasEnrolments = enrolments?.length > 0;
  return (
    <Modal
      open={open}
      accent={false}
      setOpen={closeModal}
      closeOnBackground
      className="z-10 !w-11/12 !rounded-3xl md:!w-full"
      closeButton
      position={position}
    >
      <Pending
        loading={loadingEnrolments}
        loader={<Loader className="flex min-h-[100px] items-center justify-center" />}
      >
        <div className="px-2 py-4 md:px-12 md:py-14">
          {hasEnrolments ? (
            <>
              <h2 className="pb-6 font-display text-2xl font-bold tracking-[-0.02rem] text-grey-600 md:text-[32px] md:leading-[38px]">
                {SELECT_ENROLMENT_MODAL_HEADING}
              </h2>
              <IntentIntro className="mb-5 !text-base text-grey-600 md:mb-10 md:!text-lg">
                {subtitle || 'Which enrolment do you need to manage?'}
              </IntentIntro>
              <ul className="list-none">
                {Array.isArray(enrolments) &&
                  (filterEnrolmentType
                    ? enrolments.filter((enrolment) => enrolment.type === filterEnrolmentType)
                    : enrolments || []
                  ).map((enrolment) => {
                    const isGroupSession = isGroupSessionCheck(enrolment.type);
                    let courseNameAndType = `${enrolment.student}'s ${enrolment.subject}`;
                    if (!filterEnrolmentType) {
                      courseNameAndType += ` ${isGroupSession ? '(Group)' : '(1-1)'}`;
                    }
                    const linkTo = enrolmentLinkTo(enrolment) || '';
                    const onEnrolmentSelected = () => {
                      closeModal();
                      if (openInNewTab) {
                        window.open(linkTo);
                      } else {
                        history.push(linkTo, state);
                      }
                      onSelect?.(enrolment, linkTo);
                    };

                    return (
                      <li key={enrolment.enrolmentId}>
                        <OptionCard
                          className="!bg-brown-100"
                          onClickHandler={onEnrolmentSelected}
                          openInNewTab={openInNewTab}
                        >
                          <OptionCardContent>{courseNameAndType}</OptionCardContent>
                        </OptionCard>
                      </li>
                    );
                  })}
              </ul>
            </>
          ) : (
            <>
              <IntentIntro className="mb-0">
                <span>You have no active enrolments. </span>
                <span className="ml-2">
                  <Link
                    to={PATH_HUB_MS_STUDENTS_AND_SUBJECTS}
                    className="text-blue-5 hover:text-purple-6 hover:underline"
                  >
                    Add students or subjects.
                  </Link>
                </span>
              </IntentIntro>
            </>
          )}
        </div>
      </Pending>
    </Modal>
  );
};

SelectEnrolmentModal.defaultProps = {
  openInNewTab: false,
  onSelect: undefined,
  position: 'middle',
};

export const useSelectEnrolmentModal = () => {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return {
    open,
    openModal,
    closeModal,
    SelectEnrolmentModal,
  };
};

export const SELECT_ENROLMENT_MODAL_HEADING = 'Select enrolment';
export default SelectEnrolmentModal;
